import React, { useState, useEffect, useContext, useMemo, useCallback, useRef } from "react";
import { Navigate } from "react-router-dom";
import "../../../../App.css";
import Navbar from "../../../ui/Navbar";
import Loader from "../../../ui/Loader";
import Table from "../../../ui/Table";
import { AuthContext } from "../../../../context/AuthContext";
import { swalError, swalSuccess } from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

const serverUrl = process.env.REACT_APP_SERVER_URL || "";

const getTodayDateString = () => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const localDate = new Date(now.getTime() - offset * 60000);
  return localDate.toISOString().slice(0, 10);
};

export default function OMRStats() {
  const [showDatePopup, setShowDatePopup] = useState(false);
  const [rangeOption, setRangeOption] = useState("Today");
  const [startDate, setStartDate] = useState(getTodayDateString);
  const [endDate, setEndDate] = useState(getTodayDateString);
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [displayedData, setDisplayedData] = useState(null);

  const { isAuthenticated, primaryRole } = useContext(AuthContext);
  const abortControllerRef = useRef(null);

  const breadcrumbs = [
    { name: "Home", path: "/superadmin", isLast: false },
    { name: "OMR Stats", path: "/superadmin/omr-stats", isLast: true },
  ];

  document.title = "GradeMe-AI: OMR Stats";

  const displayDateOnly = useCallback((dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString(navigator.language, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }, []);

  const getTimeRangeText = useCallback(() => {
    if (rangeOption === "Custom") {
      return startDate && endDate
        ? `${displayDateOnly(startDate)} - ${displayDateOnly(endDate)}`
        : "Select Date Range";
    }
    return rangeOption;
  }, [rangeOption, startDate, endDate, displayDateOnly]);

  const handlePresetSelect = (option) => {
    setRangeOption(option);
    const now = new Date();
    let start, end;

    const formatDate = (date) => {
      const offset = date.getTimezoneOffset();
      const localDate = new Date(date.getTime() - offset * 60000);
      return localDate.toISOString().slice(0, 10);
    };

    switch (option) {
      case "Today":
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = now;
        break;
      case "Last 24 Hours":
        start = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        end = now;
        break;
      case "This Week": {
        const dayOfWeek = now.getDay();
        const distanceToMon = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
        start = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate() - distanceToMon
        );
        end = now;
        break;
      }
      case "This Month":
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = now;
        break;
      case "Custom":
      default:
        return;
    }

    setStartDate(formatDate(start));
    setEndDate(formatDate(end));
  };

  const fetchStats = useCallback(
    async (signal) => {
      setLoading(true);
      setDisplayedData(null);
      try {
        let filterParam = "today";
        if (rangeOption === "Today") filterParam = "today";
        else if (rangeOption === "Last 24 Hours") filterParam = "last_24_hours";
        else if (rangeOption === "This Week") filterParam = "this_week";
        else if (rangeOption === "This Month") filterParam = "this_month";
        else if (rangeOption === "Custom") filterParam = "custom";

        const params = new URLSearchParams({ filterType: filterParam });

        if (startDate) params.append("startDate", startDate);
        if (endDate) params.append("endDate", endDate);

        const response = await fetchWithAuth(
          `${serverUrl}/api/omr-template/get-stats?${params.toString()}`,
          { credentials: "include", signal }
        );

        if (!response) return;
        if (!response.ok) throw new Error("Failed to fetch OMR stats");

        const result = await response.json();
        setStatsData(result);
        setError(null);
      } catch (err) {
        if (err.name === "AbortError") return;
        console.error("Error loading OMR stats:", err);
        setError(err.message || "Failed to load OMR stats");
        swalError(err.message || "Failed to load OMR stats.");
      } finally {
        setLoading(false);
      }
    },
    [rangeOption, startDate, endDate]
  );

  useEffect(() => {
    if (!rangeOption) return;

    if (startDate && endDate) {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      if (rangeOption === "Custom") {
        const timer = setTimeout(() => {
          fetchStats(controller.signal);
        }, 500);
        return () => {
          clearTimeout(timer);
          controller.abort();
        };
      } else {
        fetchStats(controller.signal);
        return () => {
          controller.abort();
        };
      }
    }
  }, [fetchStats, rangeOption, startDate, endDate]);

  // Filtered stats list
  const filteredStats = useMemo(() => {
    if (!statsData || !statsData.stats) return [];
    return statsData.stats.map((row, idx) => ({
      ...row,
      _id: row.answerPaperId || `${row.testId}_${row.studentId}_${idx}`,
    }));
  }, [statsData]);

  // Handler for table filtering (column filters, search query)
  const handleDataFiltered = useCallback((filteredList) => {
    setDisplayedData(filteredList);
  }, []);

  // Table Columns matching Usage Activity Grader layout
  const tableColumns = useMemo(
    () => [
      {
        key: "instituteName",
        label: "Institute Name",
        sortable: true,
        filter: true,
        render: (row) => (
          <div>
            <div style={{ fontWeight: 600, color: "#1e293b", fontSize: "0.92rem" }}>
              {row.instituteName || "General / Unknown"}
            </div>
            {row.instituteCode && row.instituteCode !== "-" && (
              <span
                style={{
                  fontSize: "0.75rem",
                  color: "#64748b",
                  backgroundColor: "#f1f5f9",
                  padding: "2px 8px",
                  borderRadius: "6px",
                  display: "inline-block",
                  marginTop: "3px",
                  fontWeight: 500,
                }}
              >
                Code: {row.instituteCode}
              </span>
            )}
          </div>
        ),
      },
      {
        key: "instructorName",
        label: "Instructor",
        sortable: true,
        filter: true,
        render: (row) => (
          <span style={{ color: "#334155", fontWeight: 500 }}>
            {row.instructorName || "N/A"}
          </span>
        ),
      },
      {
        key: "className",
        label: "Class",
        sortable: true,
        filter: true,
        render: (row) => (
          <span style={{ color: "#334155" }}>
            {row.className || "N/A"}
          </span>
        ),
      },
      {
        key: "testName",
        label: "Test",
        sortable: true,
        filter: true,
        render: (row) => (
          <span style={{ color: "#1e293b", fontWeight: 500 }}>
            {row.testName || "N/A"}
          </span>
        ),
      },
      {
        key: "templateName",
        label: "OMR Template",
        sortable: true,
        filter: true,
        render: (row) => (
          <div>
            <div style={{ fontWeight: 600, color: "#4338ca", fontSize: "0.92rem" }}>
              {row.templateName || "Unknown Template"}
            </div>
            {row.templateId && row.templateId !== "-" && (
              <span
                style={{
                  fontSize: "0.75rem",
                  color: "#4f46e5",
                  backgroundColor: "#eef2ff",
                  padding: "2px 8px",
                  borderRadius: "6px",
                  display: "inline-block",
                  marginTop: "3px",
                  fontWeight: 500,
                }}
              >
                ID: {row.templateId}
              </span>
            )}
          </div>
        ),
      },
      {
        key: "studentName",
        label: "Student Name",
        sortable: true,
        filter: true,
        render: (row) => (
          <span style={{ color: "#1e293b", fontWeight: 600 }}>
            {row.studentName || "[DELETED]"}
          </span>
        ),
      },
      {
        key: "gradedAt",
        label: "Graded At",
        sortable: true,
        sortBy: (row) => (row.gradedAt ? new Date(row.gradedAt).getTime() : 0),
        render: (row) => {
          if (!row.gradedAt) return <span style={{ color: "#94a3b8" }}>—</span>;
          const date = new Date(row.gradedAt);
          if (isNaN(date.getTime())) return <span style={{ color: "#94a3b8" }}>—</span>;
          return (
            <span style={{ color: "#334155", fontSize: "0.88rem", fontWeight: 500 }}>
              {date.toLocaleDateString(navigator.language, {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}{" "}
              at{" "}
              {date.toLocaleTimeString(navigator.language, {
                hour: "numeric",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
              })}
            </span>
          );
        },
      },
    ],
    []
  );

  // Export to Excel (exports active table filtered data or all available data)
  const exportToExcel = async () => {
    const dataToExport = displayedData !== null ? displayedData : filteredStats;

    if (!dataToExport || dataToExport.length === 0) {
      swalError("No OMR statistics data available to export.");
      return;
    }

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("OMR Grading Activity");

      worksheet.columns = [
        { header: "Institute Name", key: "instituteName", width: 28 },
        { header: "Institute Code", key: "instituteCode", width: 16 },
        { header: "Instructor", key: "instructorName", width: 24 },
        { header: "Class", key: "className", width: 20 },
        { header: "Test", key: "testName", width: 24 },
        { header: "OMR Template Name", key: "templateName", width: 24 },
        { header: "OMR Template ID", key: "templateId", width: 20 },
        { header: "Student Name", key: "studentName", width: 24 },
        { header: "Graded At", key: "gradedAt", width: 26 },
      ];

      const headerRow = worksheet.getRow(1);
      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: "FFFFFF" }, size: 11 };
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "74C043" },
        };
        cell.alignment = { vertical: "middle", horizontal: "center" };
      });
      headerRow.height = 28;

      dataToExport.forEach((item) => {
        const date = item.gradedAt ? new Date(item.gradedAt) : null;
        const formattedDate = date && !isNaN(date.getTime())
          ? `${date.toLocaleDateString(navigator.language, {
              day: "numeric",
              month: "short",
              year: "numeric",
            })} at ${date.toLocaleTimeString(navigator.language, {
              hour: "numeric",
              minute: "2-digit",
              second: "2-digit",
              hour12: true,
            })}`
          : "-";

        worksheet.addRow({
          instituteName: item.instituteName || "-",
          instituteCode: item.instituteCode || "-",
          instructorName: item.instructorName || "-",
          className: item.className || "-",
          testName: item.testName || "-",
          templateName: item.templateName || "-",
          templateId: item.templateId || "-",
          studentName: item.studentName || "-",
          gradedAt: formattedDate,
        });
      });

      worksheet.eachRow((row) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: "thin", color: { argb: "CBD5E1" } },
            left: { style: "thin", color: { argb: "CBD5E1" } },
            bottom: { style: "thin", color: { argb: "CBD5E1" } },
            right: { style: "thin", color: { argb: "CBD5E1" } },
          };
        });
      });

      const now = new Date();
      const dateStr = `${String(now.getDate()).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()}`;
      const filename = `OMR_Grading_Activity_${dateStr}.xlsx`;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, filename);
      swalSuccess("Excel exported successfully!");
    } catch (err) {
      console.error("Failed to export Excel:", err);
      swalError("Failed to export Excel file.");
    }
  };

  if (!isAuthenticated || primaryRole !== "superadmin") {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <Navbar pageName="OMR Template Statistics & Test Usage" breadcrumbs={breadcrumbs} />
      <div className="manage-token" style={{ maxWidth: "1400px", margin: "0 auto", padding: "20px 24px" }}>
        {/* Filter Controls Bar */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            marginBottom: "20px",
            position: "relative",
            zIndex: showDatePopup ? 1000 : 10,
          }}
        >
          {/* Date Range Selector */}
          <div className="time-range-wrapper">
            <button
              type="button"
              onClick={() => setShowDatePopup((prev) => !prev)}
              className="time-range-btn"
              style={{ marginBottom: 0 }}
              aria-expanded={showDatePopup}
              aria-haspopup="true"
            >
              <span className="btn-icon">🕒</span>
              <span className="time-range-text">{getTimeRangeText()}</span>
              <span className={`chevron ${showDatePopup ? "open" : ""}`}>▼</span>
            </button>

            {showDatePopup && (
              <>
                <div
                  className="popup-overlay"
                  onClick={() => setShowDatePopup(false)}
                />
                <div className="time-range-popup" style={{ right: 0, left: "auto" }}>
                  <div className="popup-header">
                    <h3>Select Time Range</h3>
                    <button
                      type="button"
                      className="close-btn"
                      onClick={() => setShowDatePopup(false)}
                      aria-label="Close"
                    >
                      ✕
                    </button>
                  </div>

                  <div className="preset-section">
                    <div className="preset-grid">
                      {[
                        "Today",
                        "Last 24 Hours",
                        "This Week",
                        "This Month",
                        "Custom",
                      ].map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={`preset-option ${
                            rangeOption === option ? "selected" : ""
                          }`}
                          onClick={() => handlePresetSelect(option)}
                        >
                          {option}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="custom-range-section">
                    <div className="date-inputs">
                      <div className="input-group">
                        <label htmlFor="start-date">Start Date</label>
                        <input
                          id="start-date"
                          type="date"
                          value={startDate}
                          onChange={(e) => {
                            setStartDate(e.target.value);
                            setRangeOption("Custom");
                          }}
                          className="date-input"
                        />
                      </div>
                      <div className="input-group">
                        <label htmlFor="end-date">End Date</label>
                        <input
                          id="end-date"
                          type="date"
                          value={endDate}
                          onChange={(e) => {
                            setEndDate(e.target.value);
                            setRangeOption("Custom");
                          }}
                          className="date-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Content Section */}
        {loading ? (
          <div
            style={{
              padding: "100px 0",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
            }}
          >
            <Loader style={{ position: "static", left: "auto", top: "auto", fontSize: "2.5rem", color: "#000000" }} />
          </div>
        ) : error ? (
          <div className="error-div" style={{ margin: "20px 0", padding: "16px", borderRadius: "8px", backgroundColor: "#fef2f2", color: "#b91c1c" }}>
            {error}
          </div>
        ) : (
          <div className="table-section" style={{ position: "relative" }}>
            <div
              className="table-header"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                marginBottom: "16px",
              }}
            >
              <button
                className="export-csv-btn"
                onClick={exportToExcel}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  padding: "9px 18px",
                  background: "linear-gradient(135deg, #74c043, #5ea832)",
                  backgroundColor: "#74c043",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "100px",
                  fontWeight: 600,
                  fontSize: "0.88rem",
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(116, 192, 67, 0.35)",
                  transition: "all 0.2s ease",
                }}
              >
                <i className="fa-solid fa-file-excel" style={{ fontSize: "1rem" }}></i>
                <span>Export to Excel</span>
              </button>
            </div>

            <Table
              data={filteredStats}
              columns={tableColumns}
              caption="OMR Template Grading Activity"
              pagination={true}
              pageSize={25}
              onDataFiltered={handleDataFiltered}
            />
          </div>
        )}
      </div>
    </>
  );
}

